import { useCallback, useEffect, useRef, useState } from 'react'

import { newCommandId } from './controls.js'

// Portal buttons for watch — V2-3b, ADR-0009 (P6-268). Jordan: "an easy way
// for users to click a button that has the same function as our
// /watch watching and /watch finish commands in the UI… buttons for
// marking progress." He ruled the button need not feel instant.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 A REQUEST, NEVER A WRITE — the whole shape of ADR-0009
// ═══════════════════════════════════════════════════════════════════════════
//
// This module never writes to a bot-owned store. It asks the platform
// service to leave a REQUEST (`POST /api/watch/requests`), then polls that
// request's own state (`GET /api/watch/requests/:id`) until the bot's own
// twenty-second poll has applied or refused it, or the platform service
// itself has marked it expired. Nothing here computes an outcome; every
// state below is read off the wire, never guessed at — `src/portal/
// controls.js`'s own header states the same rule for music's buttons
// ("it renders the service's answer, it never pre-judges one") and it
// binds here for the identical reason.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 SIX STATES, RENDERED DIFFERENTLY — brief §3, spec §7, P6-200's lesson
// ═══════════════════════════════════════════════════════════════════════════
//
// asked (ASKED — the server's own `pending`/`claimed` collapsed into one:
//   the caller does not need to tell them apart, only the bot's applier
//   does — ADR-0009 A1 rule 4 is the applier's own state machine), applied
//   (APPLIED), refused with a plain reason (REFUSED — `result.message` is
//   the applier's own sentence, shown as written, never re-worded here),
//   expired (EXPIRED), the platform service unreachable (UNREACHABLE), and
//   signed out — which is not a state THIS hook produces at all: a
//   signed-out row never presses a button in the first place (spec §7:
//   "Signed-out rows render the chips and no controls"), so the caller
//   guards on `you.signedIn` before ever calling `ask`.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 `client_key` IS MINTED ONCE PER PRESS AND NEVER REUSED ACROSS PRESSES
// ═══════════════════════════════════════════════════════════════════════════
//
// V2-3a §1: "one per press, so a network retry of the same press is the
// same request" — a repeated CREATE call carrying the SAME key and the
// SAME content replays with a 200 rather than creating a second request.
// `newCommandId` (`./controls.js`) is music's own "a fresh command id",
// reused rather than copied: both modules want the identical property
// (a safe, fail-loud-if-no-entropy-source UUID), and CLAUDE.md gotcha 1's
// *copies* axis is exactly the reason not to retype it here.

export const REQUEST_ENDPOINT = '/api/watch/requests'
export const requestStateEndpoint = (id) => `/api/watch/requests/${id}`

export const KIND_SET_STATUS = 'watch.set_status'
// P6-281: `watch.advance_progress` is retired. `watch.set_progress` is its
// per-member, set-shaped replacement — see `WatchManager.set_member_progress`
// and `cogs/watch/requests.py`'s own `KIND_SET_PROGRESS`.
export const KIND_SET_PROGRESS = 'watch.set_progress'
// V2-2a wired this kind through the applier and the site's own request-body
// allowlist (`functions/api/watch/_requestBody.js::KIND_SET_FAVORITE_RANK`)
// but had no site CONSUMER yet — the member profile's own ↑/↓ buttons
// (V2-2c) are the first. Set-shaped: `{rank: <positive int> | null}`, `null`
// removing the title from the member's favourites.
export const KIND_SET_FAVORITE_RANK = 'watch.set_favorite_rank'
// V2-4b wired these three kinds through the applier and the site's own
// request-body allowlist (`functions/api/watch/_requestBody.js`) but had no
// site CONSUMER yet -- `KIND_SET_FAVORITE_RANK`'s own precedent above.
// V2-4c's FinishPanel (`set_rating`/`set_review`) and Add panel/title page
// (`log`) are the first.
export const KIND_SET_RATING = 'watch.set_rating'
export const KIND_SET_REVIEW = 'watch.set_review'
export const KIND_LOG = 'watch.log'
// V2-4b2 (ADR-0009 D5): "Add to the group's list," the Add panel's own
// button -- the site's equivalent of `/watch add`. Set-shaped like every
// other kind here, and simpler: the target alone states the whole action,
// so there is no payload field at all (`AddToListControl`'s own `ask()`
// call, below, always sends `payload: {}`).
export const KIND_ADD_TITLE = 'watch.add_title'
// V2-6h1 (ADR-0009 D6): the title page's own "Link the next season" --
// `/watch link`'s effect. Wired through the applier and the site's own
// request-body allowlist (`functions/api/watch/_requestBody.js`) but has
// no site CONSUMER yet -- `KIND_SET_FAVORITE_RANK`'s own precedent above.
// This slice builds no UI (V2-6h2, after V2-6g2 merges, is the first
// consumer); `linkCommand`, below, is its Unavailable fallback, ready for
// that slice to call.
export const KIND_LINK_SEQUEL = 'watch.link_sequel'
// P6-298c (ADR-0009 D6): the series checklist's own "Add the whole series
// (nothing watched)" button -- lists a title with NOBODY marked watching,
// create-if-absent like `KIND_LOG`/`KIND_LINK_SEQUEL`. Set-shaped like every
// other kind here: the target names the title (the same provider-qualified
// grammar `logTargetForSearchHit` already builds), and the payload carries
// `{title, poster_url}` -- the checklist's own `GET /api/watch/series` data,
// used by the applier only on a genuine create (never re-fetched from a
// provider the way `watch.log`/`watch.link_sequel` do). No command builder
// exists for this kind: there is no `/watch` command with this exact effect
// (list with nobody watching), so a consumer's Unreachable fallback has
// nothing honest to name -- the "decline rather than guess" convention every
// other command builder in this module already follows (`statusCommand`'s
// own header).
export const KIND_LIST_TITLE = 'watch.list_title'
// P6-298f: the title page's own "Take this season out of its series" --
// `/watch unlink`'s effect, exactly. The target alone states the whole
// action, like `KIND_ADD_TITLE` -- no payload field at all.
//
// 🔴 This slice's first draft also shipped `watch.remove_title`
// (`/watch remove`'s effect) and it was PULLED in the fix round: ADR-0009
// line 10 records Jordan's own ruling -- "removal stays command-only
// (2026-09-20); drop likewise (2026-09-22)" -- restated as D6, "Remove and
// drop stay commands, a deliberate surface for a verdict." Unlink was never
// covered by that ruling (it deletes nothing, and is the direct inverse of
// `KIND_LINK_SEQUEL`), so it needed no override and stays. See
// docs/reports/P6-298f.md for the detail.
export const KIND_UNLINK_SEASON = 'watch.unlink_season'

export const TARGET_WATCHING = 'watching'
// P6-282, Jordan verbatim: "the buttons are 'I'm watching' and 'I've
// stopped watching'." `stopped` is the ordinary, day-to-day "I'm off
// this one" target now -- `TARGET_WATCHED` stays a real, reachable
// target (a movie's "I watched it", and set-shaped idempotency for
// whatever reached `watched` some other way), but the site's own status
// CONTROL no longer offers it as a button for a series -- see
// `WatchPanel.jsx::MemberActionRow`.
export const TARGET_STOPPED = 'stopped'
export const TARGET_WATCHED = 'watched'

export const ASKED = 'asked'
export const APPLIED = 'applied'
export const REFUSED = 'refused'
export const EXPIRED = 'expired'
export const UNREACHABLE = 'unreachable'
// V2-6f2: `useWatchRequestBatch`'s own pseudo-state, below — a batch item
// that was never attempted at all because an earlier item in the same run
// hit the platform service's 30-a-minute cap (or the door was unreachable).
// Never produced by `useWatchRequest`'s own six states; see that hook's own
// header for why this file has exactly six, and the batch hook's header for
// why a seventh, batch-only word does not change that count.
export const UNSENT = 'unsent'

/** How often the request's own state is re-read while it is `asked`.
 * Slower than music's 2s projection poll (nothing here needs to feel
 * live-live — Jordan's own ruling) and fast enough that a handful of polls
 * cover the bot's twenty-second cycle comfortably. */
export const POLL_MS = 3_000

/** An outer safety bound, comfortably ABOVE the platform service's own
 * `watch.request_ttl_seconds` default (300s, `docs/reports/V2-3a.md` §3) —
 * so the ORDINARY case always resolves from the server's own expiry, not
 * from this cap. Defensive only: a request that is somehow still `pending`
 * or `claimed` past its own TTL is a bug in the platform service, not
 * something this hook should paper over by polling forever. */
const MAX_WAIT_MS = 360_000

/** Consecutive failed polls tolerated before surfacing UNREACHABLE — one
 * dropped poll is a blip, not an outage; the CREATE call itself has no such
 * tolerance, because that is the member's own direct action and deserves an
 * immediate, honest answer. */
const MAX_CONSECUTIVE_POLL_FAILURES = 3

const GENERIC_REFUSAL = 'That was refused.'

function messageOf(request) {
  const message = request?.result?.message
  return typeof message === 'string' && message.trim() !== '' ? message : GENERIC_REFUSAL
}

/** The server's own `RequestRead.state` -> this hook's UI states. `pending`
 * and `claimed` both read ASKED — ADR-0009 A1 rule 4's claim/pending split
 * is the APPLIER's own state machine, not a distinction a button needs to
 * render. Anything unrecognised also reads ASKED, so a future server-side
 * state this build has never heard of degrades to "still working on it"
 * rather than throwing or rendering nothing. */
function uiStateOf(serverState) {
  if (serverState === APPLIED) return APPLIED
  if (serverState === REFUSED) return REFUSED
  if (serverState === EXPIRED) return EXPIRED
  return ASKED
}

const TERMINAL = new Set([APPLIED, REFUSED, EXPIRED])

/**
 * POST the request. Never throws — every failure resolves to an answer
 * this hook can render.
 *
 * 🔴 V2-6f2: `status` ADDED to the two failure returns below, additively.
 * `useWatchRequest.ask()` (the only caller before this slice) destructures
 * only `ok`/`unreachable`/`message`/`request` and never reads `status`, so
 * its own behaviour is byte-identical -- the brief's own "keep
 * `useWatchRequest`'s own behaviour unchanged," met by adding a field
 * nothing existing looks at rather than a second copy of this function.
 * `useWatchRequestBatch`, below, is the new reader: it needs to tell a rate
 * limit (429) apart from an ordinary refusal (a 422, say) to decide whether
 * to keep going or stop the whole batch early, and the status code is the
 * only thing that tells the two apart -- `unreachable` alone does not (a
 * 429 is not unreachable, it is a labelled "not yet").
 */
async function postRequest(body, fetchImpl) {
  let response
  try {
    response = await fetchImpl(REQUEST_ENDPOINT, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    })
  } catch {
    return { ok: false, unreachable: true }
  }
  // 502 is `functions/_lib/trips.js::downstreamStatus`'s own translation of
  // "the platform service did not answer" (a genuine 401 there is OUR
  // misconfiguration, mapped the same way) — the one status this hook reads
  // as UNREACHABLE rather than a refusal.
  if (response.status === 502) return { ok: false, unreachable: true, status: 502 }
  if (!response.ok) {
    let message = GENERIC_REFUSAL
    try {
      const text = await response.text()
      if (text.trim() !== '') message = text.trim()
    } catch {
      // keep the generic sentence
    }
    return { ok: false, unreachable: false, message, status: response.status }
  }
  let json
  try {
    json = await response.json()
  } catch {
    return { ok: false, unreachable: true }
  }
  if (!json || typeof json !== 'object' || typeof json.id !== 'string' || typeof json.state !== 'string') {
    return { ok: false, unreachable: true }
  }
  return { ok: true, request: json }
}

/** GET the request's current state. Never throws, same discipline. */
async function getRequestState(requestId, fetchImpl) {
  let response
  try {
    response = await fetchImpl(requestStateEndpoint(requestId), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
  } catch {
    return { ok: false, unreachable: true }
  }
  if (response.status === 502) return { ok: false, unreachable: true }
  if (!response.ok) return { ok: false, unreachable: false }
  let json
  try {
    json = await response.json()
  } catch {
    return { ok: false, unreachable: true }
  }
  if (!json || typeof json !== 'object' || typeof json.state !== 'string') {
    return { ok: false, unreachable: true }
  }
  return { ok: true, request: json }
}

/**
 * One press's lifecycle: `ask(body)` -> ASKED -> a terminal state.
 *
 * `status` is `null` before anything is pressed (renders nothing has
 * failed — `src/portal/watch.js`'s own "for the first moment of every load
 * NOTHING HAS FAILED" rule, applied to a button rather than a panel load).
 * `message` carries the refused reason, and only then.
 *
 * One instance per control (the "I'm in" button, the "I've watched it"
 * button, and the +1 stepper each hold their own) — there is no
 * cross-control queue-position hazard here the way `useMusicControls` has
 * for the music queue, so nothing is gained by serialising them, and a
 * member pressing two different controls on the same title at once is
 * ordinary use the manager's own file-save serialises correctly either way.
 */
export function useWatchRequest() {
  const [status, setStatus] = useState(null)
  const [message, setMessage] = useState(null)
  const mounted = useRef(true)
  const pollTimer = useRef(null)
  const failedPolls = useRef(0)

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
      if (pollTimer.current !== null) {
        window.clearTimeout(pollTimer.current)
        pollTimer.current = null
      }
    }
  }, [])

  const stopPolling = useCallback(() => {
    if (pollTimer.current !== null) {
      window.clearTimeout(pollTimer.current)
      pollTimer.current = null
    }
  }, [])

  const schedulePoll = useCallback((requestId, startedAtMs, fetchImpl) => {
    pollTimer.current = window.setTimeout(async () => {
      if (!mounted.current) return
      const answer = await getRequestState(requestId, fetchImpl)
      if (!mounted.current) return

      if (!answer.ok) {
        if (answer.unreachable) {
          failedPolls.current += 1
          if (failedPolls.current >= MAX_CONSECUTIVE_POLL_FAILURES) {
            setStatus(UNREACHABLE)
            setMessage(null)
            return
          }
        }
        schedulePoll(requestId, startedAtMs, fetchImpl)
        return
      }
      failedPolls.current = 0

      const ui = uiStateOf(answer.request.state)
      if (TERMINAL.has(ui)) {
        setStatus(ui)
        setMessage(ui === REFUSED ? messageOf(answer.request) : null)
        return
      }
      if (Date.now() - startedAtMs > MAX_WAIT_MS) {
        setStatus(UNREACHABLE)
        setMessage(null)
        return
      }
      schedulePoll(requestId, startedAtMs, fetchImpl)
    }, POLL_MS)
  }, [])

  /**
   * `body` is `{kind, target, payload}` — `client_key` is minted here, once
   * per call, never passed in (see the module header).
   */
  const ask = useCallback(
    async (body, fetchImpl = globalThis.fetch) => {
      stopPolling()
      failedPolls.current = 0
      setStatus(ASKED)
      setMessage(null)

      const created = await postRequest({ ...body, client_key: newCommandId() }, fetchImpl)
      if (!mounted.current) return
      if (!created.ok) {
        if (created.unreachable) {
          setStatus(UNREACHABLE)
          setMessage(null)
        } else {
          setStatus(REFUSED)
          setMessage(created.message)
        }
        return
      }

      const ui = uiStateOf(created.request.state)
      if (TERMINAL.has(ui)) {
        setStatus(ui)
        setMessage(ui === REFUSED ? messageOf(created.request) : null)
        return
      }
      schedulePoll(created.request.id, Date.now(), fetchImpl)
    },
    [schedulePoll, stopPolling],
  )

  const reset = useCallback(() => {
    stopPolling()
    setStatus(null)
    setMessage(null)
  }, [stopPolling])

  return { status, message, ask, reset, busy: status === ASKED }
}

// ═══════════════════════════════════════════════════════════════════════════
// V2-6f2 — SEVERAL REQUESTS AS ONE UNIT ("log the whole series")
// ═══════════════════════════════════════════════════════════════════════════
//
// `useWatchRequest`, above, is unchanged by everything below this line — a
// single control's own one-request lifecycle. The checklist can send up to
// 23 requests from one tap (brief §0: 12 logs + 11 links, the series
// lookup's own `MAX_LISTED_SEASONS`), so it needs N independent lifecycles
// rather than N mounted copies of a hook meant for one button. This reuses
// `postRequest`/`getRequestState`/`uiStateOf`/`messageOf`/`TERMINAL` — the
// SAME private wire functions `useWatchRequest.ask()` already calls — so
// there is one implementation of "what a request's wire shape means," never
// two (CLAUDE.md gotcha 1's *copies* axis). Nothing above this comment was
// touched to make that possible; `postRequest`'s own header explains the one
// additive change (a `status` field neither `ask()` nor anything before this
// slice ever read).
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 CREATION IS SEQUENTIAL, AND STOPS EARLY ON A RATE LIMIT OR A DEAD DOOR
// ═══════════════════════════════════════════════════════════════════════════
//
// Brief §2.4: "A 429 on creating any request marks those as not sent...
// Offer a retry for exactly the unsent ones." Attempting every remaining ask
// after the platform service has already said "too many at once" would only
// produce a wall of identical 429s, so the FIRST 429 — or an unreachable
// platform service, the same "the door itself is in trouble right now"
// signal `postRequest`'s own 502 branch already reads as UNREACHABLE — stops
// the loop, and every ask not yet attempted is marked `UNSENT` alongside it,
// never sent at all. An ordinary REFUSAL (the applier's own rules declining
// ONE request, ADR-0009 D3) says nothing about the next one, so the loop
// keeps going past it — a refusal is an outcome, not a door problem.
//
// Once a request is accepted, its poll is per-item and fully concurrent with
// every other item's — nothing about a GET carries the creation-side rate
// cap — each mirroring `useWatchRequest`'s own ask -> poll -> terminal shape
// exactly, including its `MAX_WAIT_MS` outer bound and its
// `MAX_CONSECUTIVE_POLL_FAILURES` tolerance for a dropped poll.

/**
 * One ask -> one item's terminal fate, reusing `postRequest`/`getRequestState`
 * exactly as `useWatchRequest` does. A free function (not a hook) so `run`
 * and `retryUnsent` below share one implementation rather than two similar
 * loops — the same reason `logTargetForSearchHit` is called from both
 * `AddToListControl` and `LogItControl` rather than rebuilt in each.
 *
 * `asks` is `[{key, kind, target, payload}]` — `key` is stable across a
 * retry (it is how a later `retryUnsent` finds "the same ask" again);
 * `client_key` is minted fresh on every attempt, including a retry, exactly
 * as `useWatchRequest.ask()` already mints one fresh per press (the module
 * header's own rule: "one per press, so a network retry of the same press is
 * the same request" — a RETRY here is a new press, not a resend).
 */
async function runAsksSequentially({ asks, updateItem, schedulePoll, mounted, fetchImpl }) {
  let stopped = false
  for (const ask of asks) {
    if (!mounted.current) return
    if (stopped) {
      updateItem(ask.key, { status: UNSENT, message: null })
      continue
    }
    // eslint-disable-next-line no-await-in-loop -- deliberately sequential;
    // see the module header on why concurrent creation is the wrong shape
    // for a rate-limited door.
    const created = await postRequest(
      { kind: ask.kind, target: ask.target, payload: ask.payload, client_key: newCommandId() },
      fetchImpl,
    )
    if (!mounted.current) return
    if (!created.ok) {
      if (created.unreachable || created.status === 429) {
        stopped = true
        updateItem(ask.key, { status: UNSENT, message: null })
        continue
      }
      updateItem(ask.key, { status: REFUSED, message: created.message })
      continue
    }

    const ui = uiStateOf(created.request.state)
    if (TERMINAL.has(ui)) {
      updateItem(ask.key, { status: ui, message: ui === REFUSED ? messageOf(created.request) : null })
      continue
    }
    updateItem(ask.key, { status: ASKED, message: null })
    schedulePoll(ask.key, created.request.id, Date.now(), fetchImpl)
  }
}

/**
 * `run(asks)` sends every ask in order; `retryUnsent()` re-attempts only
 * whatever is currently `UNSENT`, leaving every settled item untouched — "a
 * retry never duplicates an accepted one" (brief §3) falls out of that by
 * construction, since `retryUnsent` never even looks at a non-`UNSENT` item.
 *
 * `items` is `[{key, kind, target, payload, status, message}]`, in the same
 * order `run` was called with. `status` is `null` for an item this hook has
 * not yet reached (still queued behind an earlier one in the same
 * sequential pass) and one of `ASKED`/`APPLIED`/`REFUSED`/`EXPIRED`/
 * `UNREACHABLE`/`UNSENT` once it has.
 */
export function useWatchRequestBatch() {
  const [items, setItems] = useState([])
  const itemsRef = useRef([])
  itemsRef.current = items
  const mounted = useRef(true)
  const pollFailures = useRef(new Map())
  const pollTimers = useRef(new Map())

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
      for (const timerId of pollTimers.current.values()) window.clearTimeout(timerId)
      pollTimers.current.clear()
    }
  }, [])

  const updateItem = useCallback((key, patch) => {
    setItems((previous) => previous.map((item) => (item.key === key ? { ...item, ...patch } : item)))
  }, [])

  const schedulePoll = useCallback(
    (key, requestId, startedAtMs, fetchImpl) => {
      const timerId = window.setTimeout(async () => {
        pollTimers.current.delete(key)
        if (!mounted.current) return
        const answer = await getRequestState(requestId, fetchImpl)
        if (!mounted.current) return

        if (!answer.ok) {
          if (answer.unreachable) {
            const failures = (pollFailures.current.get(key) ?? 0) + 1
            pollFailures.current.set(key, failures)
            if (failures >= MAX_CONSECUTIVE_POLL_FAILURES) {
              updateItem(key, { status: UNREACHABLE, message: null })
              return
            }
          }
          schedulePoll(key, requestId, startedAtMs, fetchImpl)
          return
        }
        pollFailures.current.set(key, 0)

        const ui = uiStateOf(answer.request.state)
        if (TERMINAL.has(ui)) {
          updateItem(key, { status: ui, message: ui === REFUSED ? messageOf(answer.request) : null })
          return
        }
        if (Date.now() - startedAtMs > MAX_WAIT_MS) {
          updateItem(key, { status: UNREACHABLE, message: null })
          return
        }
        schedulePoll(key, requestId, startedAtMs, fetchImpl)
      }, POLL_MS)
      pollTimers.current.set(key, timerId)
    },
    [updateItem],
  )

  const run = useCallback(
    async (asks, fetchImpl = globalThis.fetch) => {
      // 🔴 THE WHOLE ASK IS SPREAD IN, NOT JUST `key`/`kind`/`target`/`payload`.
      // A caller (`SeriesChecklist`) may attach its own display-only fields —
      // a human `label`, a `command` for the Unreachable mono fallback
      // (brief §2.4: "surface it where the Add panel already does") — and
      // this hook carries them through unread, the same "opaque to this
      // layer" treatment `target`/`payload` already get from the wire side.
      setItems(asks.map((ask) => ({ ...ask, status: null, message: null })))
      await runAsksSequentially({ asks, updateItem, schedulePoll, mounted, fetchImpl })
    },
    [updateItem, schedulePoll],
  )

  const retryUnsent = useCallback(
    async (fetchImpl = globalThis.fetch) => {
      // Re-derived from the CURRENT items, not from whatever `run` was
      // originally called with, so a caller's own display fields
      // (`label`/`command`) survive a retry unchanged — only `status`/
      // `message` are dropped, since this is a fresh attempt.
      const unsentAsks = itemsRef.current
        .filter((item) => item.status === UNSENT)
        .map(({ status, message, ...ask }) => ask)
      if (unsentAsks.length === 0) return
      await runAsksSequentially({ asks: unsentAsks, updateItem, schedulePoll, mounted, fetchImpl })
    },
    [updateItem, schedulePoll],
  )

  return { items, run, retryUnsent }
}

/** Plain-language, fixed sentences for the states that do not carry their
 * own message (`REFUSED` always does, from the wire). A lookup, never an
 * interpolation — `functions/_lib/respond.js::STAGE_QUESTION`'s own rule —
 * so the only strings this module can show are the literals below. */
export const ACTION_SENTENCE = Object.freeze({
  [ASKED]: 'Asking the bot…',
  [APPLIED]: 'Done ✓',
  [EXPIRED]: "The bot didn't pick that up. Try again, or use the command in Discord.",
  [UNREACHABLE]: 'Buttons are unavailable right now — /watch in Discord still works.',
})

/** The sentence to render for a non-null, non-`APPLIED` status (the caller
 * decides what to do once APPLIED — usually a brief confirmation, then a
 * refresh and a reset back to the live controls). */
export function actionSentence(status, message) {
  if (status === REFUSED) return message ?? GENERIC_REFUSAL
  return ACTION_SENTENCE[status] ?? null
}

/**
 * V2-8a: the status control's own honest command, for `ActStrip`'s
 * Unavailable state ("the controls are replaced by the equivalent Discord
 * command in mono," the brief's own words) — `progressCommand`
 * (`watchTitle.js`) is the equivalent for advancing an episode; this is its
 * sibling for setting a member's own status, built the identical
 * named-parameter way (`cogs/watch/cog.py::watch_watching`/`watch_stopped`/
 * `watch_watched` each take one real parameter, `title`).
 *
 * `target` is one of `TARGET_WATCHING`/`TARGET_STOPPED`/`TARGET_WATCHED` —
 * the exact string the failed press asked for (`MemberActionRow`'s own
 * `lastTargetRef`), never guessed: a command naming an action nobody
 * actually pressed would be a command that does not describe what
 * happened (RECONCILIATION §5: "never print a command that does not
 * exist"). `null` when there is nothing to name yet (nothing pressed, or
 * an unrecognised target), which the caller reads as "no honest command to
 * offer" — the same `null`-means-decline convention `progressCommand` uses.
 */
export function statusCommand(subscription, target) {
  if (target !== TARGET_WATCHING && target !== TARGET_STOPPED && target !== TARGET_WATCHED) return null
  const title = typeof subscription?.title === 'string' ? subscription.title : ''
  return `/watch ${target} title:${title}`
}

/**
 * V2-2c: the member profile's own favourite ↑/↓ buttons, `ActStrip`'s
 * Unavailable fallback — the SAME named-parameter shape `statusCommand`
 * above already uses, for the SAME reason (RECONCILIATION §5: "any command
 * the site prints must resolve against the bot's catalog"). The real
 * command is `/watch favorite`, singular — never `/watch favorites`, the
 * name the design's own prototype prints and RECONCILIATION §5 explicitly
 * names as one of three that do not exist.
 *
 * `rank === null` names the real command for CLEARING a favourite
 * (`/watch unfavorite`) rather than a favourite-with-no-rank sentence that
 * does not exist — the ↑/↓ buttons this hook currently backs never pass
 * `null` (there is nowhere to move "up" or "down" from a removal), but a
 * command builder that only knows how to describe half of the kind it
 * names would be a trap for the next caller.
 */
export function favoriteRankCommand(title, rank) {
  const name = typeof title === 'string' ? title : ''
  return rank === null ? `/watch unfavorite title:${name}` : `/watch favorite title:${name} rank:${rank}`
}

/**
 * V2-4c: the FinishPanel's own Unavailable fallback -- brief §2, "the
 * unavailable state replaces the controls with `/watch rate` and
 * `/watch review` in mono." Named-parameter shape, matching every command
 * builder above (`cogs/watch/cog.py::watch_rate`'s own two real
 * parameters, `title` and `rating`).
 *
 * `rating === null` (nothing picked yet) has no honest command to offer --
 * `/watch rate` always needs a number -- so this returns `null`, the same
 * "decline rather than guess" convention `progressCommand`/`statusCommand`
 * already use.
 */
export function ratingCommand(title, rating) {
  if (!Number.isInteger(rating)) return null
  const name = typeof title === 'string' ? title : ''
  return `/watch rate title:${name} rating:${rating}`
}

/** V2-4c: `/watch review`'s own Unavailable fallback. Unlike `ratingCommand`,
 * there is no value to name -- `cogs/watch/cog.py::watch_review` opens a
 * modal and takes no text parameter at all -- so this never returns `null`;
 * the command is the same regardless of what the member has typed. */
export function reviewCommand(title) {
  const name = typeof title === 'string' ? title : ''
  return `/watch review title:${name}`
}

/**
 * V2-4c: `/watch log`'s own Unavailable fallback, for the Add panel's
 * search-result flow and the title page's own "Log it as watched" note.
 * `whenValue` is the `watched_when.value` the member picked (a `"YYYY-MM"`
 * or `"YYYY"` string), or `null`/`undefined` for "Don't remember" --
 * `cogs/watch/cog.py::watch_log`'s own `when` parameter is optional, so
 * omitting it (rather than inventing a placeholder) is the honest command
 * for that choice.
 */
export function logCommand(title, whenValue) {
  const name = typeof title === 'string' ? title : ''
  return whenValue ? `/watch log title:${name} when:${whenValue}` : `/watch log title:${name}`
}

/**
 * V2-6h1: `/watch link`'s own Unavailable fallback, for the title page's
 * own "Link the next season" control (V2-6h2, no UI yet in this slice).
 * `nextTitle` is the season/sequel's own title, or `null`/`undefined` for
 * a manual CLEAR -- `cogs/watch/cog.py::watch_link`'s own `next` parameter
 * is optional and left blank to clear an existing link, so omitting it
 * (rather than inventing a placeholder) is the honest command for that
 * choice, in `logCommand`'s own style just above.
 */
export function linkCommand(title, nextTitle) {
  const name = typeof title === 'string' ? title : ''
  const nextName = typeof nextTitle === 'string' ? nextTitle : ''
  return nextName ? `/watch link title:${name} next:${nextName}` : `/watch link title:${name}`
}

/**
 * V2-4c, narrowed by V2-4b2: `watch.add_title`'s own Unavailable
 * fallback (RECONCILIATION item 11's "controls replaced by the command
 * in mono") -- BEFORE V2-4b2 this was the Add panel's PERMANENT stand-in
 * for "Add to the group's list," since the kind did not exist yet; now
 * that it does, `AddToListControl` (`AddPanel.jsx`) reaches for this only
 * when `useWatchRequest()` itself reports UNREACHABLE, the same role
 * every other command builder in this module already plays.
 * `cogs/watch/cog.py::watch_add` takes one parameter, `query` -- a search
 * STRING, never a provider id, because the command re-searches and offers
 * its own picker; there is no way to deep-link it straight to one hit.
 */
export function addCommand(query) {
  return `/watch add query:${typeof query === 'string' ? query : ''}`
}

/**
 * P6-298f: `/watch unlink`'s own Unavailable fallback, `ManagePanel.jsx`'s
 * Unlink control. `cogs/watch/cog.py::watch_unlink` takes one real
 * parameter, `title` -- `reviewCommand`'s own shape, one seam over.
 */
export function unlinkCommand(title) {
  const name = typeof title === 'string' ? title : ''
  return `/watch unlink title:${name}`
}

/**
 * V2-4c: `watch.log`'s own target shape for a search result, exactly as
 * `docs/reports/V2-4b.md` §2 documents it and `cogs/watch/requests.py::
 * _parse_log_target` parses it (`^tmdb:(tv|movie):(\d+)$|^anilist:(\d+)$`)
 * -- built here, once, rather than at each call site, so a future third
 * provider only needs this function touched.
 *
 * V2-4b2: `watch.add_title` reuses this SAME target shape (brief §2.1:
 * "the same provider-qualified form watch.log uses") -- `AddToListControl`
 * (`AddPanel.jsx`) calls this function too, never a second target-builder
 * for the identical string shape.
 *
 * `hit` is one `/api/watch/search` result (`{provider, kind, id, ...}`,
 * `docs/reports/V2-4a.md` §1): TMDB always states its kind (`tv`/`movie`,
 * the two id spaces collide in VALUE while naming different titles);
 * AniList never does (every AniList hit is `kind: 'anime'`, the sole call
 * site the bot's own target-builder comment names). `null` for anything
 * this function does not recognise, rather than a malformed string the
 * applier would refuse anyway -- fail loud at the one place that already
 * knows the shape, not three requests later.
 */
export function logTargetForSearchHit(hit) {
  if (!hit || typeof hit !== 'object') return null
  const { provider, kind, id } = hit
  if (!Number.isInteger(id)) return null
  if (provider === 'tmdb') return kind === 'tv' || kind === 'movie' ? `tmdb:${kind}:${id}` : null
  if (provider === 'anilist') return `anilist:${id}`
  return null
}
